import { type MustGateEntry, SLOT_ACTION_TARGET, TOOL_SLOT_ACTION } from '@zerohuman/shared'

/**
 * An Express tool's binding template as the Staff form types it (#587): the must-gate list one entry a line, the
 * `git.*` preset one `name=value` a line, and the action map one `action=tool` a line (#956). The API checks each
 * again; these only turn text into what it is sent as.
 */

/** One entry a line: a tool name as it is, an entry narrowed by `match` as its JSON. */
export function mustGateText(entries: readonly MustGateEntry[] | undefined): string {
    return (entries ?? []).map(entry => (typeof entry === 'string' ? entry : JSON.stringify(entry))).join('\n')
}

/** The list a form's text names: each line a tool name, or `{ "tool": …, "match": … }`. Blank lines are dropped. */
export function mustGateFromText(text: string): MustGateEntry[] {
    return text
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
            if (!line.startsWith('{')) {
                return line
            }
            try {
                return JSON.parse(line) as MustGateEntry
            } catch {
                throw new Error('express_tool_must_gate_invalid')
            }
        })
}

/** One `name=value` a line. */
export function propsPresetText(props: Record<string, string> | undefined): string {
    return Object.entries(props ?? {})
        .map(([key, value]) => `${key}=${value}`)
        .join('\n')
}

/** The preset a form's text names: `name=value` a line, each name once. A value may hold `=` itself. */
export function propsPresetFromText(text: string): Record<string, string> {
    const props: Record<string, string> = {}
    for (const line of text
        .split('\n')
        .map(item => item.trim())
        .filter(Boolean)) {
        const at = line.indexOf('=')
        const key = line.slice(0, at).trim()
        const value = line.slice(at + 1).trim()
        if (at < 1 || !key || !value || Object.hasOwn(props, key)) {
            throw new Error('express_tool_props_preset_invalid')
        }
        props[key] = value
    }
    return props
}

/** One `action=tool` a line: the action a task names under a slot, and the tool it is on this server, pinned or not. */
export function slotActionsText(actions: Record<string, string> | undefined): string {
    return Object.entries(actions ?? {})
        .map(([action, tool]) => `${action}=${tool}`)
        .join('\n')
}

/**
 * The action map a form's text names: `action=tool` a line, each action once, the action lowercase words joined by
 * dots (`pr.merge`), the tool a bare name with an optional pin (`pull_request_write#method=merge`), never a prefix. A
 * pin may hold `=` itself.
 */
export function slotActionsFromText(text: string): Record<string, string> {
    const actions: Record<string, string> = {}
    for (const line of text
        .split('\n')
        .map(item => item.trim())
        .filter(Boolean)) {
        const at = line.indexOf('=')
        const action = line.slice(0, at).trim()
        const tool = line.slice(at + 1).trim()
        if (
            at < 1 ||
            !TOOL_SLOT_ACTION.test(action) ||
            !SLOT_ACTION_TARGET.test(tool) ||
            Object.hasOwn(actions, action)
        ) {
            throw new Error('express_tool_slot_actions_invalid')
        }
        actions[action] = tool
    }
    return actions
}
